import { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * LiveBuildBadge — the small right-aligned build stamp on
 * /admin/diagnostics. Shows the FULL build_fingerprint (never truncated)
 * and the pod-start time, both fetched LIVE from /api/health on every
 * render. Bypasses browser + edge caches via a cache-busting query
 * string + `Cache-Control: no-cache`. Also re-fetches on window focus
 * so the owner can eyeball a deploy immediately after the redeploy
 * completes.
 *
 * Prior surface (superseded 2026-07-13):
 *   `git: <fp-…12-char slice>` — snapshotted from the diagnostics run
 *   (60s cached). This lied about deploy state for up to 60s + the
 *   ingress cache window every time.
 */
export default function LiveBuildBadge() {
  const [fp, setFp] = useState(null);
  const [podStart, setPodStart] = useState(null);
  const [err, setErr] = useState(null);
  const [fetchedAt, setFetchedAt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchHealth = async () => {
      try {
        const r = await axios.get(`${API}/api/health`, {
          headers: { 'Cache-Control': 'no-cache' },
          params: { _: Date.now() },
        });
        if (cancelled) return;
        setFp(r?.data?.build_fingerprint || null);
        setPodStart(r?.data?.process_started_at || null);
        setErr(null);
        setFetchedAt(new Date());
      } catch (e) {
        if (cancelled) return;
        setErr(e?.message || 'health fetch failed');
      }
    };
    fetchHealth();
    const onFocus = () => fetchHealth();
    window.addEventListener('focus', onFocus);
    return () => { cancelled = true; window.removeEventListener('focus', onFocus); };
  }, []);

  return (
    <div data-testid="live-build-badge" className="font-mono">
      <div>
        build:{' '}
        <code data-testid="live-build-fp" className="text-[11px] break-all">
          {fp || (err ? '(fetch failed)' : 'loading…')}
        </code>
      </div>
      <div className="text-[11px] opacity-80">
        pod started:{' '}
        <span data-testid="live-build-pod-start">
          {podStart ? podStart.replace('T', ' ').replace(/\.\d+/, '').replace('+00:00', ' UTC') : '?'}
        </span>
      </div>
      {fetchedAt && (
        <div className="text-[10px] opacity-60">
          fetched {fetchedAt.toLocaleTimeString()} · re-fetches on focus
        </div>
      )}
    </div>
  );
}
